import { useState, useCallback, useMemo } from 'react'
import {
  DriverWorkflowStep,
  DriverUploadStaging,
  DriverUpdateRow,
  DriverBulkUploadResult,
  FieldConflictResolution,
  UnmatchedDriverAction,
  SimpleProcessingProgress,
  Upload
} from '@/types'
import type { DriverMaster } from '@/types/driverMaster'

export interface DriverUploadWorkflowState {
  step: DriverWorkflowStep
  driverUploadStaging: DriverUploadStaging | null
  originalUpload: Upload | null
  loading: boolean
  identifierMapping: Record<string, string>
  profileFieldMapping: Record<string, string>
  driverUpdateRows: DriverUpdateRow[]
  availableDrivers: DriverMaster[]
  conflictResolutions: Map<string, FieldConflictResolution[]>
  unmatchedActions: Map<string, UnmatchedDriverAction>
  isProcessing: boolean
  processingProgress: SimpleProcessingProgress | null
  uploadResult: DriverBulkUploadResult | null
  error: string | null
}

const createInitialState = (): DriverUploadWorkflowState => ({
  step: 'field-mapping',
  driverUploadStaging: null,
  originalUpload: null,
  loading: true,
  identifierMapping: {},
  profileFieldMapping: {},
  driverUpdateRows: [],
  availableDrivers: [],
  conflictResolutions: new Map(),
  unmatchedActions: new Map(),
  isProcessing: false,
  processingProgress: null,
  uploadResult: null,
  error: null
})

export function useDriverUploadWorkflowState() {
  const [state, setState] = useState<DriverUploadWorkflowState>(createInitialState())

  const setStep = useCallback((step: DriverWorkflowStep) => {
    setState(prev => ({ ...prev, step }))
  }, [])

  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, loading }))
  }, [])

  const setProcessingProgress = useCallback((progress: SimpleProcessingProgress | null) => {
    setState(prev => ({ ...prev, processingProgress: progress }))
  }, [])

  const updateState = useCallback((updates: Partial<DriverUploadWorkflowState>) => {
    setState(prev => ({ ...prev, ...updates }))
  }, [])

  const resetState = useCallback(() => {
    setState(createInitialState())
  }, [])

  return useMemo(
    () => ({
      ...state,
      state,
      setStep,
      setLoading,
      setProcessingProgress,
      updateState,
      resetState
    }),
    [state, setStep, setLoading, setProcessingProgress, updateState, resetState]
  )
}
